<template>
    <div class="app-virtual-table">
        <div
            v-if="$slots.toolbar"
            class="table-toolbar">
            <slot name="toolbar" />
        </div>

        <div
            ref="scrollContainer"
            class="scroll-container"
            :class="{ 'loading-blur': shouldBlur }"
            @scroll="handleScroll">
            <div
                v-if="shouldBlur"
                class="loading-overlay" />

            <v-progress-circular
                v-if="loading && items.size === 0"
                class="initial-load-spinner"
                indeterminate
                color="primary" />

            <virtual-table-header
                :headers="headers"
                :sort-by="sortBy"
                :sort-desc="sortDesc"
                :column-widths="columnWidths"
                :cell-height="cellHeight"
                :resizing-column="resizingColumn"
                :recently-resized-column="recentlyResizedColumn"
                @sort="handleSort"
                @resize-start="handleResizeStart" />

            <virtual-table-body
                :items="items"
                :headers="headers"
                :total-items="totalItems"
                :column-widths="columnWidths"
                :cell-height="cellHeight"
                :scroll-top="scrollTop"
                :container-height="containerHeight"
                :header-height="headerHeight"
                @cell-click="handleCellClick">
                <template
                    v-for="header in headers"
                    :slot="`cell.${header.value}`"
                    slot-scope="slotProps">
                    <slot
                        :name="`item.${header.value}`"
                        v-bind="slotProps" />
                </template>
            </virtual-table-body>

            <div
                v-if="activeLoadingRanges.size > 0 && !loading"
                class="loading-more-indicator">
                <v-progress-circular
                    indeterminate
                    size="20"
                    width="2"
                    color="primary" />
                <span>Loading items...</span>
            </div>

            <div
                v-if="!loading && totalItems === 0 && initialLoadAttempted"
                class="no-data-placeholder">
                <span>No data available</span>
            </div>
        </div>

        <virtual-table-row
            v-if="totals"
            class="totals-row"
            :row-index="0"
            :item="totalRow"
            :is-placeholder="false"
            :headers="headers"
            :column-widths="columnWidths"
            :cell-height="cellHeight"
            :clickable="false">
            <template
                v-for="header in headers"
                :slot="`cell.${header.value}`"
                slot-scope="slotProps">
                <slot
                    :name="`item.${header.value}`"
                    v-bind="slotProps" />
            </template>
        </virtual-table-row>

        <div
            v-if="totalItems > 0"
            class="table-footer">
            <div class="result-info">
                {{ totalItems.toLocaleString() }} result{{ totalItems === 1 ? "" : "s" }}
            </div>
        </div>
    </div>
</template>

<script>
import VirtualTableBody from './VirtualTableBody.vue';
import VirtualTableHeader from './VirtualTableHeader.vue';
import VirtualTableRow from './VirtualTableRow.vue';
import virtualScrollMixin from '../mixins/virtualScrollMixin';
import columnWidthsMixin from '../mixins/columnWidthsMixin';
import dataLoadingMixin from '../mixins/dataLoadingMixin';

export default {
    components: {
        VirtualTableBody,
        VirtualTableHeader,
        VirtualTableRow
    },
    mixins: [virtualScrollMixin, columnWidthsMixin, dataLoadingMixin],
    props: {
        items: {
            type: Map,
            required: true
        },
        headers: {
            type: Array,
            required: true
        },
        totalItems: {
            type: Number,
            required: true
        },
        totals: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        },
        loadedRanges: {
            type: Array,
            default: () => []
        },
        sortBy: {
            type: String,
            default: null
        },
        sortDesc: {
            type: Boolean,
            default: false
        },
        cellHeight: {
            type: Number,
            default: 45
        }
    },
    data() {
        return {
            headerHeight: 48,
            resizeObserver: null
        };
    },
    computed: {
        shouldBlur() {
            return this.loading && this.items.size > 0 && this.activeLoadingRanges.size === 0;
        },
        totalRow() {
            if (!this.totals) return null;

            // Create a row object that only includes totals for appropriate fields
            const row = {};

            // Add totals values only for fields that have the hasTotal flag set to true
            this.headers.forEach(header => {
                if (header.value !== 'actions' && header.hasTotal === true) {
                    const key = header.value;
                    const totalValue = this.totals[key];
                    if (totalValue !== undefined && totalValue !== null) {
                        row[key] = totalValue;
                    }
                }
            });

            return row;
        }
    },
    watch: {
        totals() {
            // Don't force - let the normal skip logic work
            this.recalculateColumnWidths(false).catch(() => {});
        },
        totalItems(newVal, oldVal) {
            if (oldVal !== 0 || newVal === 0) return;

            this.$nextTick(() => {
                this.loadVisibleRange();
            });
        },
        loadedRanges: {
            deep: true,
            handler(newRanges, oldRanges) {
                this.updateLoadingRanges(newRanges, this.activeLoadingRanges);

                // Recalculate column widths when first data loads - but only if we don't have widths yet
                const isFirstDataLoad = (!oldRanges || oldRanges.length === 0) && newRanges.length > 0;
                const needsInitialWidths = Object.keys(this.columnWidths).length === 0;
                const shouldRecalculate = isFirstDataLoad &&
                    needsInitialWidths &&
                    this.manuallyResizedColumns.size === 0;

                if (shouldRecalculate) {
                    this.$nextTick(() => {
                        this.recalculateColumnWidths(false).catch(() => {});
                    });
                }

                // Check if there are still unloaded items in view after update
                this.$nextTick(() => {
                    this.loadVisibleRange();
                });
            }
        },

        items: {
            handler(newItems) {
                // When items first populate, calculate widths - but only once
                const needsInitialWidths = Object.keys(this.columnWidths).length === 0;
                if (newItems.size > 0 && needsInitialWidths) {
                    this.$nextTick(() => {
                        this.recalculateColumnWidths(false).catch(() => {});
                    });
                }
            },
            immediate: false
        }
    },
    async mounted() {
        this.updateContainerHeight();
        this.setupResizeObserver();
        this.performInitialLoad();
    },
    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        this.$parent.$off('data-loaded');
    },
    methods: {
        // Override handleScroll from virtualScrollMixin to add data loading
        handleScroll() {
            if (!this.$refs.scrollContainer) return;
            this.scrollTop = this.$refs.scrollContainer.scrollTop;
            this.loadVisibleRange();
        },

        // Event handlers
        handleCellClick({ item }) {
            this.$emit('click:row', item);
        },

        handleSort({ sortBy, sortDesc }) {
            this.$emit('update:sort-by', sortBy);
            this.$emit('update:sort-desc', sortDesc);
        },

        // Resize observer
        setupResizeObserver() {
            if (!this.$refs.scrollContainer) return;

            this.resizeObserver = new ResizeObserver(() => {
                this.updateContainerHeight();
                // Don't force - let the normal skip logic work during loading
                this.recalculateColumnWidths(false).catch(() => {});
            });
            this.resizeObserver.observe(this.$refs.scrollContainer);
        },

        performInitialLoad() {
            if (this.totalItems === 0) return;

            this.$nextTick(() => {
                this.loadVisibleRange();
            });
        }
    }
};
</script>

<style scoped>
.app-virtual-table {
    display: flex;
    flex-direction: column;
    border: 0.07rem solid var(--app-border, #e0e0e0);
    border-radius: var(--app-border-radius, 0.25rem);
    background: var(--app-surface, white);
    width: 100%;
    height: 100%;
    overflow: hidden;
}

.table-toolbar {
    flex-shrink: 0;
    border-bottom: 0.07rem solid var(--app-table-border, #e0e0e0);
    padding: 1rem;
}

.scroll-container {
    position: relative;
    flex: 1 1 auto;
    width: 100%;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
}

.scroll-container.loading-blur {
    filter: blur(2px);
    opacity: 0.5;
    pointer-events: none;
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
    pointer-events: none;
}

.initial-load-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.no-data-placeholder {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 3rem;
    width: 100%;
    color: var(--app-muted-text, rgba(0, 0, 0, 0.54));
    font-size: 1.1rem;
    text-align: center;
}

.loading-more-indicator {
    display: flex;
    position: fixed;
    bottom: 4rem;
    left: 50%;
    justify-content: center;
    align-items: center;
    gap: 0.75rem;
    transform: translateX(-50%);
    z-index: 100;
    box-shadow: 0 0.13rem 0.53rem rgba(0, 0, 0, 0.15);
    border-radius: var(--app-border-radius, 0.25rem);
    background: var(--app-surface, white);
    padding: 0.75rem 1.5rem;
    color: var(--app-muted-text, rgba(0, 0, 0, 0.54));
    font-size: 1rem;
}

.table-footer {
    display: flex;
    justify-content: center;
    border-top: 0.07rem solid var(--app-border, #e0e0e0);
    background: var(--color-background-secondary, #f5f5f5);
    padding: 0.75rem 1rem;
}

.result-info {
    color: var(--color-text-secondary, rgba(0, 0, 0, 0.6));
    font-size: 0.875rem;
    white-space: nowrap;
}

.totals-row {
    flex-shrink: 0;
    border-top: 0.07rem solid var(--app-border, #e0e0e0);
    background: var(--app-surface, white);
}
</style>
